import { eventTag } from "./shared";
import { formatWorldDate } from "../../journal/journal";
import { type EventId, type WorldDateView } from "../../world/types";

/*
 * What somebody said, and what the street said (chunk U-0's split of
 * `panels.ts`).
 *
 * Two instances of one class: an answer to something the player did, and a line
 * the town said on its own. The hold and fade are here because they are this
 * surface's judgement — seven seconds is a sentence read at walking pace.
 *
 * # The band (chunk U-1)
 *
 * The overheard instance is the kit's **subtitle track** since the graduation:
 * centred at the bottom of the frame, gold hairlines above and below, the
 * speaker and what they said on one line with a gold colon between them. The
 * answer keeps its panel. Three things about that are decisions rather than
 * markup:
 *
 * - **the two share a column and the band is always above the answer**
 *   (`hud/hud.ts`'s `speech`, `ui/hud.css`'s `.hud__speech`). The elements are
 *   built in the order they are *asked about* — `querySelector` for a dialogue
 *   text has meant the answer since Phase 3 and means it still — and the column
 *   reverses them for the screen;
 * - **the band never out-sizes the answer.** Its line is the kit's 1.1rem
 *   against the answer's 1.15rem, which is the owner's rule as two numbers:
 *   ambient word must not out-shout the conversation in front of you;
 * - **the clocks did not move.** Seven seconds and 1.6 to fade, the same for
 *   both, exactly as they were.
 */

/** How long a spoken line stays at full strength, and how long it takes to go. */
const DIALOGUE_HOLD_SECONDS = 7;
const DIALOGUE_FADE_SECONDS = 1.6;

/**
 * What the band calls itself, in the corner of the track.
 *
 * The kit's `.subtitle-hint` and the lab's own word. It is client copy and it is
 * allowed to be, on the same terms as the empty shelf's line: it names the
 * channel rather than saying anything about the world, and there is no record
 * for the simulation to be asked about — the fact it states is that *nobody
 * asked for this line*, which is a fact about the client's own staging
 * (`witness/staging.ts`) and about nothing else.
 */
const OVERHEARD_HINT = "overheard";

/**
 * One line of speech: who said it, what they said, and which record it was
 * about.
 *
 * One line and not a conversation — the cut list is explicit that a dialogue
 * *system* is out of scope, and names the reason: `activity_event_conversationst`
 * carries a 254-value topic machine (units §6.2), and building the front end for
 * it before there is anything behind it is how a prototype acquires a subsystem
 * it cannot feed.
 *
 * Two *instances*, though, since the polish pass, told apart by `variant`. The
 * answer line sits bottom centre and only ever holds what the player asked for;
 * the overheard line sits at the right edge and holds the street speaking on
 * its own (`witness/staging.ts`). They were one slot, and the arithmetic of one
 * slot was unreadable by construction: a staged voice lands every 5.5 seconds
 * and the hold is 7, so on a reaction morning an answer could never survive to
 * the end of being read.
 */
export class DialogueLine {
  private readonly element: HTMLElement;
  private readonly speakerElement: HTMLElement;
  private readonly textElement: HTMLElement;
  private readonly sourceElement: HTMLElement;
  private readonly memoryElement: HTMLElement;

  /** Seconds this line has left before it starts fading, then while it fades. */
  private remaining = 0;

  constructor(root: HTMLElement, variant: "answer" | "overheard" = "answer") {
    const band = variant === "overheard";

    this.element = document.createElement("div");
    // The kit composes alongside the client's own names (plan D2): the answer is
    // a soft panel — glass and a hairline, no corner brackets, because brackets
    // say *a place you stopped at* and this goes away by itself — and the band
    // is the subtitle track. Neither keeps the kit's `backdrop-filter`; that is
    // cancelled in `ui/hud.css` for every HUD line, per plan decision D6.
    this.element.className = band
      ? "hud__dialogue hud__dialogue--overheard subtitle-track"
      : "hud__dialogue game-panel game-panel--soft";
    this.element.hidden = true;

    this.speakerElement = document.createElement("div");
    this.speakerElement.className = "hud__dialogue-speaker speaker-label";

    this.textElement = document.createElement("div");
    this.textElement.className = "hud__dialogue-text";

    this.sourceElement = document.createElement("div");
    this.sourceElement.className = "hud__dialogue-source";

    // The anchor's line, under the record's. Its own element rather than more
    // text in the source line because it is a different *kind* of statement —
    // that one is what the world said, this is what the player has already done
    // (`journal/people.ts`) — and because it is the one line here a player is
    // meant to read rather than check.
    this.memoryElement = document.createElement("div");
    this.memoryElement.className = "hud__dialogue-memory";

    if (band) {
      // **The caption is one sentence with a name in front of it**, so the
      // speaker, the colon and the words go inside one line element rather than
      // stacking. The colon is its own node and not part of the speaker, because
      // three checks and two smoke scripts compare `.hud__dialogue-speaker`'s
      // text to the name the simulation spelled and a punctuation mark stuck to
      // the end of it would be this client editing a name.
      const colon = document.createElement("span");

      colon.className = "hud__dialogue-colon colon";
      colon.textContent = ":";

      // And the one word in this file the client typed: the CHANNEL. What the
      // sentence cannot say about itself is that nobody was asked for it — the
      // street is talking and the player happened to be within earshot — and
      // that is the whole difference between this element and the one below it.
      const hint = document.createElement("span");

      hint.className = "hud__dialogue-hint subtitle-hint";
      hint.textContent = OVERHEARD_HINT;

      const line = document.createElement("div");

      line.className = "hud__dialogue-band subtitle-line";
      line.append(this.speakerElement, colon, this.textElement);
      this.element.append(hint, line, this.sourceElement, this.memoryElement);
    } else {
      this.element.append(
        this.speakerElement,
        this.textElement,
        this.sourceElement,
        this.memoryElement,
      );
    }

    root.append(this.element);
  }

  /**
   * Whether a line is up. Read by {@link Hud} to decide whether a subtitle is
   * holding the centre of the frame, and by the checks.
   *
   * The fade counts as up: a caption at a tenth of its opacity is still the
   * thing in the middle of the screen, and a yield rule that snapped back the
   * instant the hold ended would flash the prompt twice in two seconds.
   */
  get open(): boolean {
    return this.remaining > 0;
  }

  /**
   * Shows a line, restarting the fade.
   *
   * `about` is the record it came out of, and it is shown rather than kept
   * quietly: this is the channel the exit criterion calls "an overheard line",
   * and the tag is how a playtester checks it against the log.
   *
   * `text` may carry its own line breaks and they are kept (`styles.css` sets
   * `white-space: pre-line` on the text element). A spoken line never has any;
   * an inscription is all breaks — a name over two dates — and flattening one
   * into a sentence is the difference between reading a grave and being told
   * about it.
   *
   * `memory` is what the *player* brings to the line — somebody they have met
   * who is from the town this news is out of (Phase 4 decision 6). It is shown
   * under the record rather than woven into the sentence, because the sentence
   * is the simulation's and nothing in this client edits one.
   */
  say(
    speaker: string,
    text: string,
    about: EventId | null,
    learned: WorldDateView | null,
    memory: string | null = null,
  ): void {
    const known = learned === null ? "" : ` · known here since ${formatWorldDate(learned)}`;

    this.speakerElement.textContent = speaker;
    this.textElement.textContent = text;
    this.sourceElement.textContent = about === null ? "" : `${eventTag(about)}${known}`;
    this.memoryElement.textContent = memory ?? "";
    this.memoryElement.hidden = memory === null;
    this.remaining = DIALOGUE_HOLD_SECONDS + DIALOGUE_FADE_SECONDS;
    this.element.hidden = false;
    this.element.style.opacity = "1";
  }

  /**
   * Advances the fade.
   *
   * Called every frame from `hud/hud.ts` *before* its refresh throttle, because
   * a fade that only advanced ten times a second would step rather than fade.
   */
  update(frameSeconds: number): void {
    if (this.remaining <= 0) {
      return;
    }

    this.remaining -= frameSeconds;

    if (this.remaining <= 0) {
      this.element.hidden = true;

      return;
    }

    if (this.remaining < DIALOGUE_FADE_SECONDS) {
      this.element.style.opacity = (this.remaining / DIALOGUE_FADE_SECONDS).toFixed(3);
    }
  }

  /** Hides it now, whatever the fade was doing. */
  clear(): void {
    this.remaining = 0;
    this.element.hidden = true;
  }
}
